import environment from '@/environment'
import type { ControllerDescription } from '@/features/controller/types'
import type { Site } from '@/features/site/types/site'

type ControllersDashboardTarget = {
  /** Grafana's `var-location`. Falls back to `All` when we do not know it. */
  location?: string | null
  /** Grafana's `var-partner`. Falls back to `All` when we do not know it. */
  partner?: string | null
  /** Real equipment numbers only — callers filter, so a missing one is a decision at the call site. */
  equipmentNumbers: number[]
}

/**
 * The controllers dashboard URL, built from what it actually needs. Kept separate from the `Site` +
 * `ControllerDescription` signature below because callers outside this feature hold neither, and
 * assembling a stand-in object with one real field and the rest invented would be worse.
 */
function buildControllersDashboardUrl({
  location,
  partner,
  equipmentNumbers,
}: ControllersDashboardTarget): string | null {
  // No controller to point at, so no dashboard: the URL would load and filter to nothing, which
  // reads as "no data" rather than "we do not know which controller this is".
  if (equipmentNumbers.length === 0) {
    return null
  }

  return (
    `${environment.observability.grafanaBaseUrl}/d/d-FmJFqmz/sympower-controllers` +
    `?orgId=1` +
    `&var-environment=${environment.environmentName}` +
    `&var-location=${location || 'All'}` +
    `&var-partner=${partner || 'All'}` +
    equipmentNumbers.map((equipmentNumber) => `&var-controller=${equipmentNumber}`).join('') +
    `&var-firmware=All` +
    `&var-networkMode=All`
  )
}

/**
 * Null when no controller has an equipment number. Mapping an undefined one straight through puts
 * `var-controller=undefined` in the query string.
 */
export function makeGrafanaLinkForControllersDashboard(
  site: Site | null,
  controllers: Pick<ControllerDescription, 'equipmentNumber'>[],
): string | null {
  return buildControllersDashboardUrl({
    location: site?.location,
    partner: site?.symbolicName,
    equipmentNumbers: controllers
      .map((controller) => controller.equipmentNumber)
      .filter(
        (equipmentNumber): equipmentNumber is number => equipmentNumber !== null && equipmentNumber !== undefined,
      ),
  })
}

/**
 * The same dashboard, for a caller holding an equipment number and a location rather than a `Site`
 * and a `ControllerDescription`. Null without an equipment number, mirroring
 * `makeControllerLogSearchUrl`: a URL carrying `var-controller=null` looks live and filters to
 * nothing. `partner` is not taken, since a resource would only ever supply the `All` fallback.
 */
export function makeGrafanaLinkForControllerEquipmentNumber(
  equipmentNumber: number | null | undefined,
  location: string | null | undefined,
): string | null {
  if (equipmentNumber === null || equipmentNumber === undefined) {
    return null
  }

  return buildControllersDashboardUrl({ location, equipmentNumbers: [equipmentNumber] })
}
